// src/hooks/useProximityDoctors.js
"use client";

import { useState, useCallback, useMemo } from "react";
import i18n from "../../i18n/config";

const NEARBY_API_BASE = import.meta.env.VITE_NEARBY_API_BASE_URL || "";

export const getCurrentBrowserLocation = (options = {}) =>
  new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new Error("Geolocation is not supported"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ latitude: coords.latitude, longitude: coords.longitude }),
      reject,
      options,
    );
  });

export default function useProximityDoctors({
  initialRadiusKm = 5,
  initialCategory = "doctors",
} = {}) {
  const [userLocation, setUserLocation] = useState(null);
  const [radiusKm, setRadiusKm] = useState(initialRadiusKm);
  const [category, setCategory] = useState(initialCategory || "doctors");

  const [results, setResults] = useState([]);  
  const [mode, setMode] = useState(null);      

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const searchNearby = useCallback(
    async ({
      location,
      radius,
      specialty,
      governorate,
      limit = 100,
    } = {}) => {
      const loc = location || userLocation;
      if (!loc?.latitude || !loc?.longitude) {
        console.warn("[useProximityDoctors] searchNearby: no location");
        return;
      }

      setLoading(true);
      setError("");

      try {
        if (!NEARBY_API_BASE) {
          throw new Error("Nearby search service is not configured for this deployment.");
        }
        const payload = {
          latitude: loc.latitude,
          longitude: loc.longitude,
          category: category || "doctors", // DOIT matcher la collection Mongo
          radius: typeof radius === "number" ? radius : radiusKm,
          limit,
        };

        if (specialty && specialty !== "All specialties") {
          payload.specialty = specialty;
        }
        if (governorate && governorate.trim()) {
          payload.governorate = governorate.trim();
        }


        const resp = await fetch(`${NEARBY_API_BASE}/nearby`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const data = await resp.json();

        if (!resp.ok || !data?.success) {
          setError(
            data?.error || i18n.t("doctorFinder.messages.errorNearby")
          );
          setResults([]);
          setMode("nearby");
        } else {
          setResults(Array.isArray(data.results) ? data.results : []);
          setMode("nearby");
        }
      } catch (e) {
        console.error("[useProximityDoctors] nearby network error:", e);
        setError(i18n.t("doctorFinder.messages.errorNearbyNetwork"));
        setResults([]);
        setMode("nearby");
      } finally {
        setLoading(false);
      }
    },
    [category, radiusKm, userLocation]
  );

  const requestUserLocationAndNearby = useCallback(
    async ({ specialty, governorate, radius, limit } = {}) => {
      setLoading(true);
      setError("");

      if (!navigator.geolocation) {
        setError(i18n.t("doctorFinder.messages.errorGeoUnsupported"));
        setLoading(false);
        return;
      }

      // If browser/site settings already block geolocation, fail fast with a clear message.
      try {
        if (navigator.permissions?.query) {
          const status = await navigator.permissions.query({ name: "geolocation" });
          if (status?.state === "denied") {
            setError(
              i18n.t("doctorFinder.messages.errorGeoDeniedSettings") ||
                "Location access is blocked in your browser settings for this site."
            );
            setLoading(false);
            return;
          }
        }
      } catch (e) {
        // Continue and rely on getCurrentPosition callback errors.
      }

      try {
        const loc = await getCurrentBrowserLocation({
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });
        setUserLocation(loc);

        // une fois la position connue → recherche proximité
        searchNearby({ location: loc, radius, specialty, governorate, limit });
      } catch (err) {
        console.error("[useProximityDoctors] geolocation error:", err);
        if (err?.code === 1) {
          setError(
            i18n.t("doctorFinder.messages.errorGeoDeniedSettings") ||
              "Location permission denied. Please allow location for this site in browser settings."
          );
        } else if (err?.code === 2) {
          setError(
            i18n.t("doctorFinder.messages.errorGeoUnavailable") ||
              "Location information is unavailable right now."
          );
        } else if (err?.code === 3) {
          setError(
            i18n.t("doctorFinder.messages.errorGeoTimeout") ||
              "Location request timed out. Please try again."
          );
        } else {
          setError(i18n.t("doctorFinder.messages.errorGeoDenied"));
        }
        setLoading(false);
      }
    },
    [searchNearby]
  );

  const searchInDb = useCallback(
    async ({
      query = "",
      specialty,
      governorate,
      limit = 500,
    } = {}) => {
      setLoading(true);
      setError("");

      try {
        const payload = {
          query,
          category: category || "doctors",
          limit,
        };

        if (specialty && specialty !== "All specialties") {
          payload.specialty = specialty;
        }
        if (governorate && governorate.trim()) {
          payload.governorate = governorate.trim();
        }


        const resp = await fetch(`${NEARBY_API_BASE}/search/manual`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const data = await resp.json();

        if (!resp.ok || !data?.success) {
          setError(
            data?.error || i18n.t("doctorFinder.messages.errorSearch")
          );
          setResults([]);
          setMode("db");
        } else {
          setResults(Array.isArray(data.results) ? data.results : []);
          setMode("db");
        }
      } catch (e) {
        console.error("[useProximityDoctors] manual search error:", e);
        setError(i18n.t("doctorFinder.messages.errorSearchNetwork"));
        setResults([]);
        setMode("db");
      } finally {
        setLoading(false);
      }
    },
    [category]
  );

  /* ------------------------------------------------------------------ */
  /* 🔹 3) MAPPING COMMUN POUR LA CARTE & DoctorInfoCard                */
  /* ------------------------------------------------------------------ */
  const doctorsNearby = useMemo(
    () =>
      results
        .map((place, idx) => {
          const coords = place.coordinates || {};
          const lat = coords.lat;
          const lng = coords.lng;

          // ⚠️ On ne garde que ceux qui ont des coordonnées valides
          if (lat == null || lng == null) return null;

          return {
            id: place.id || place.place_id || `doctor-${idx}`,
            name: place.name || "Doctor",
            specialty: place.specialty || "Doctor",
            address: place.address || "",
            phone: place.phone_number || "",
            governorate: place.governorate || "",
            rating: place.rating ?? null,
            is_open_now:
              typeof place.is_open_now === "boolean"
                ? place.is_open_now
                : null,
            distanceText: place.distance_text || "", // dispo en mode nearby
            lat,
            lng,
            avatar:
              place.image_url ||
              "/src/assets/images/doctor-placeholder.png",
            google_maps_url: place.google_maps_url || "",
          };
        })
        .filter(Boolean),
    [results]
  );

  /* ------------------------------------------------------------------ */
  /* 🔹 4) Retour du hook                                               */
  /* ------------------------------------------------------------------ */
  return {
    // état
    userLocation,
    radiusKm,
    category,
    mode,          // "nearby" ou "db" → pour info si tu veux adapter l'UI
    loading,
    error,
    rawResults: results,

    // liste normalisée pour MapSection / DoctorInfoCard
    doctors: doctorsNearby,

    // actions
    setRadiusKm,
    setCategory,

    // filtrage BD (sans tenir compte de la géolocalisation)
    searchInDb,

    // géolocalisation + recherche de proximité
    searchNearby,
    requestUserLocationAndNearby,
  };
}